import { useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Container, Stack, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { Brand } from '../components/Brand';
import { useApp } from '../context/AppContext';
import {
  clearMicrosoftOAuthSession,
  completeMicrosoftMailboxConnection,
  getMicrosoftOAuthSession,
} from '../services/mailboxService';

export function MicrosoftOAuthCallbackPage() {
  const navigate = useNavigate();
  const { refreshAppState } = useApp();
  const started = useRef(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (started.current) return;
    started.current = true;

    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    const state = params.get('state');
    const providerError = params.get('error_description') || params.get('error');
    window.history.replaceState({}, document.title, window.location.pathname);

    if (providerError) {
      setError(providerError);
      return;
    }
    if (!code || !state) {
      setError('Microsoft did not return the required authorization details.');
      return;
    }

    (async () => {
      try {
        const session = getMicrosoftOAuthSession();
        const result = await completeMicrosoftMailboxConnection({ code, state });
        clearMicrosoftOAuthSession();
        await refreshAppState();
        navigate(result.returnTo || session?.returnTo || '/setup', { replace: true });
      } catch (err) {
        setError(err?.message || 'Could not connect Outlook.');
      }
    })();
  }, [navigate, refreshAppState]);

  return (
    <Box minHeight="100vh" sx={{ bgcolor: 'background.default', py: 8 }}>
      <Container maxWidth="sm">
        <Stack spacing={3} alignItems="center" textAlign="center">
          <Brand />
          {error ? (
            <>
              <Typography variant="h2" sx={{ fontSize: { xs: 32, sm: 42 } }}>Outlook was not connected</Typography>
              <Alert severity="error" sx={{ width: '100%', textAlign: 'left' }}>{error}</Alert>
              <Button variant="contained" onClick={() => navigate('/setup', { replace: true })}>Return to setup</Button>
            </>
          ) : (
            <>
              <CircularProgress />
              <Typography variant="h3" sx={{ fontSize: 26 }}>Finishing your Outlook connection…</Typography>
              <Typography color="text.secondary">You will return to Nudge automatically.</Typography>
            </>
          )}
        </Stack>
      </Container>
    </Box>
  );
}
